/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_offices-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_offices-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_offices-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, code
 * - Sortable fields: sort_order, created_at, updated_at
 * - Required fields: id, code, name, sort_order, archived, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_offices-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_offices-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_offices.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_offices-entity-config.ts
 * Public fields: 7/7 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupOfficesClient, LookupOfficesQueryOptions } from '../generated_clients/generated-lookup_offices-api-client.js';
import {
  LookupOfficesInsert,
  LookupOfficesUpdate,
  LookupOfficesFieldConfig
} from '../../types/generated_schemas/generated-lookup_offices-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_offices_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupOfficesKeys = {
  all: ['lookup_offices'] as const,
  lists: () => [...lookupOfficesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupOfficesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupOfficesKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupOfficesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupOfficesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupOfficesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupOfficesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupOfficesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupOfficesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupOfficesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupOfficesClient();
};

/**
 * Hook to get all lookup_offices with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_offices data
 */
export const useLookupOfficesList = (options?: LookupOfficesQueryOptions) => {
  const client = useLookupOfficesClient();
  
  return useQuery({
    queryKey: lookupOfficesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_office by ID
 *
 * @param id - The lookup_office ID
 * @returns React Query result with single lookup_office data
 */
export const useLookupOffices = (id: string) => {
  const client = useLookupOfficesClient();
  
  return useQuery({
    queryKey: lookupOfficesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_offices
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_offices_business_hooks/
 */

/**
 * Hook to create a new lookup_office
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, code, name, sort_order, archived, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_office
 */
export const useCreateLookupOffices = () => {
  const client = useLookupOfficesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupOfficesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupOfficesKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_office
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_office
 */
export const useUpdateLookupOffices = () => {
  const client = useLookupOfficesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupOfficesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupOfficesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupOfficesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_office
 *
 * @returns React Query mutation for deleting lookup_office
 */
export const useDeleteLookupOffices = () => {
  const client = useLookupOfficesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupOfficesKeys.all });
    },
  });
};

/**
 * Hook to count lookup_offices with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, code
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupOfficesCount = (filter?: Partial<Record<typeof LookupOfficesFieldConfig.filterable[number], any>>) => {
  const client = useLookupOfficesClient();
  
  return useQuery({
    queryKey: lookupOfficesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupOfficesFieldConfig = () => {
  return LookupOfficesFieldConfig;
};